import React from 'react'

const Dictionary = () => {
    return (
        <div className='container'>
            <div class="editor-contents">
                              <h1>Python Dictionary </h1>
              
              
                              <p class="editor-contents__short-description">In this tutorial, you'll learn everything about Python dictionaries; how they are created, accessing, adding, removing elements from them and various built-in methods.</p>
                 
   
    
    <div id="node-48" class="node node-python clearfix" about="/python-programming/dictionary" typeof="sioc:Item foaf:Document">

  
      <span property="dc:title" content="Python Dictionary " class="rdf-meta element-hidden"></span>
  
  <div class="content">
      <p id="what">Python dictionary is an unordered collection of items. Each item of a dictionary has a <code>key/value</code> pair.</p>

<p>Dictionaries are optimized to retrieve values when the key is known.</p>

<hr/>
<h2 id="create">Creating Python Dictionary</h2>

<p>Creating a dictionary is as simple as placing items inside curly braces <code>&#123;&#125;</code> separated by commas.</p>

<p>An item has a <code>key</code> and a corresponding <code>value</code> that is expressed as a pair (<strong>key: value</strong>).</p>

<p>While the values can be of any data type and can repeat, keys must be of immutable type (string,number or tuple with immutable elements) and must be unique.</p>
<div className='containers'>
<pre style={{maxHeight: '600px'}}><code class="python hljs"><span class='comment'># empty dictionary</span><br />
my_dict = &#123;&#125;<br />
<br />
<span class='comment'># dictionary with integer keys</span><br />
my_dict =&#123;<span class="hljs-number">1</span>: <span class="hljs-string">'apple'</span>, <span class="hljs-number">2</span>: <span class="hljs-string">'ball'</span>&#125;<br />
<br />
<span class='comment'># dictionary with mixed keys</span><br />
my_dict =&#123;<span class="hljs-string">'name'</span>: <span class="hljs-string">'John'</span>, <span class="hljs-number">1</span>: [<span class="hljs-number">2</span>, <span class="hljs-number">4</span>, <span class="hljs-number">3</span>]&#125;<br />
<br />
<span class='comment'># using dict()</span><br />
my_dict = dict(&#123;<span class="hljs-number">1</span>:<span class="hljs-string">'apple'</span>, <span class="hljs-number">2</span>:<span class="hljs-string">'ball'</span>&#125;)<br />
<br />
<span class='comment'># from sequence having each item as a pair</span><br />
my_dict = dict([(<span class="hljs-number">1</span>,<span class="hljs-string">'apple'</span>), (<span class="hljs-number">2</span>,<span class="hljs-string">'ball'</span>)])</code></pre><br />
<br />
</div>
<p>As you can see from above, we can also create a dictionary using the built-in <code>dict()</code> function.</p>

<hr/>
<h2 id="access">Accessing Elements from Dictionary</h2>

<p>While indexing is used with other data types to access values, a dictionary uses <code>keys</code>. Keys can be used either inside square brackets <code>[]</code> or with the <code>get()</code> method.</p>

<p>If we use the square brackets <code>[]</code>, <code>KeyError</code> is raised in case a key is not found in the dictionary. On the other hand, the <code>get()</code> method returns <code>None</code> if the key is not found.</p>
<div className='containers'>
<pre class="python-exec" style={{maxHeight: '600px'}}><code class="python hljs"><span class='comment'># get vs [] for retrieving elements</span> <br />
my_dict =&#123;<span class="hljs-string">'name'</span>: <span class="hljs-string">'Jack'</span>, <span class="hljs-string">'age'</span>: <span class="hljs-number">26</span>&#125;<br />
<br />
<span class='comment'># Output: Jack</span><br />
<span class="library">print</span>(my_dict[<span class="hljs-string">'name'</span>])<br />
<br />
<span class='comment'># Output: 26</span><br />
<span class="library">print</span>(my_dict.get(<span class="hljs-string">'age'</span>))<br />
<br />
<span class='comment'># Trying to access keys which doesn't exist throws error</span><br />
<span class='comment'># Output None</span><br />
<span class="library">print</span>(my_dict.get(<span class="hljs-string">'address'</span>))<br /><br />

<span class='comment'># KeyError</span><br />
<span class="library">print</span>(my_dict[<span class="hljs-string">'address'</span>])</code></pre><br />
</div>
<div className='output'>
<p><strong>Output</strong></p>

<pre><samp>{`Jack
26
None
Traceback (most recent call last):
  File "&lt;string&gt;", line 15, in &lt;module&gt;
    print(my_dict['address'])
KeyError: 'address'`}</samp></pre></div>

<hr/>
<h2 id="change">Changing and Adding Dictionary elements</h2>

<p>Dictionaries are mutable. We can add new items or change the value of existing items using an assignment operator.</p>

<p>If the key is already present, then the existing value gets updated. In case the key is not present, a new (<strong>key: value</strong>) pair is added to the dictionary.</p>
<div className='containers'>
<pre class="python-exec" style={{maxHeight: '600px'}}><code class="python hljs"><span class='comment'># Changing and adding Dictionary Elements</span><br />
my_dict =&#123;<span class="hljs-string">'name'</span>: <span class="hljs-string">'Jack'</span>, <span class="hljs-string">'age'</span>: <span class="hljs-number">26</span>&#125;<br />
<br />
<span class='comment'># update value</span><br />
my_dict[<span class="hljs-string">'age'</span>] = <span class="hljs-number">27</span><br />
<br />
<span class='comment'>#Output:&#123;'age': 27, 'name': 'Jack'&#125;</span><br />
<span class="library">print</span>(my_dict)<br />
<br />
<span class='comment'># add item</span><br />
my_dict[<span class="hljs-string">'address'</span>] = <span class="hljs-string">'Downtown'</span><br />
<br />
<span class='comment'># Output:&#123;'address': 'Downtown', 'age': 27, 'name': 'Jack'&#125;</span><br />
<span class="library">print</span>(my_dict)</code></pre><br />
</div>
<div className='output'>
<p><strong>Output</strong></p>

<pre><samp>{`{'name': 'Jack', 'age': 27}
{'name': 'Jack', 'age': 27, 'address': 'Downtown'}`}</samp></pre>
</div>
<hr/>
<h2 id="delete">Removing elements from Dictionary</h2>

<p>We can remove a particular item in a dictionary by using the <code>pop()</code> method. This method removes an item with the provided <code>key</code> and returns the <code>value</code>.</p>

<p>The <code>popitem()</code> method can be used to remove and return an arbitrary <code>(key, value)</code> item pair from the dictionary. All the items can be removed at once, using the <code>clear()</code> method.</p>

<p>We can also use the <code>del</code> keyword to remove individual items or the entire dictionary itself.</p>
<div className='containers'>
<pre class="python-exec" ><code class="python hljs"><span class='comment'># Removing elements from a dictionary</span><br />
<br />
<span class='comment'># create a dictionary</span><br />
squares = &#123;<span class="hljs-number">1</span>: <span class="hljs-number">1</span>, <span class="hljs-number">2</span>: <span class="hljs-number">4</span>, <span class="hljs-number">3</span>: <span class="hljs-number">9</span>, <span class="hljs-number">4</span>: <span class="hljs-number">16</span>, <span class="hljs-number">5</span>: <span class="hljs-number">25</span>&#125;<br />
<br />
<span class='comment'># remove a particular item, returns its value</span><br />
<span class='comment'># Output: 16</span><br />
<span class="library">print</span>(squares.pop(<span class="hljs-number">4</span>))<br />
<br />
<span class='comment'># Output: &#123;1: 1, 2: 4, 3: 9, 5: 25&#125;</span><br />
<span class="library">print</span>(squares)<br />
<br />
<span class='comment'># remove an arbitrary item, return (key,value)</span><br />
<span class='comment'># Output: (5, 25)</span><br />
<span class="library">print</span>(squares.popitem())<br />
<br />
<span class='comment'># Output: &#123;1: 1, 2: 4, 3: 9&#125;</span><br />
<span class="library">print</span>(squares)<br />
<br />
<span class='comment'># remove all items</span><br />
squares.clear()<br />
<br />
<span class='comment'># Output: &#123;&#125;</span><br />
<span class="library">print</span>(squares)<br />
<br />
<span class='comment'># delete the dictionary itself</span><br />
<span class="library">del</span> squares<br />
<br />
<span class='comment'># Throws Error</span><br />
<span class="library">print</span>(squares)</code></pre></div><br />
<br />
<p></p><div id="block-inject-1" class="block-inject block-inject-1"><br />
    </div><div class="clearfix"></div><p><strong>Output</strong></p>
    <div className='output'>
<pre><samp>{`16
{1: 1, 2: 4, 3: 9, 5: 25}
(5, 25)
{1: 1, 2: 4, 3: 9}
{}
Traceback (most recent call last):
  File "<string>", line 30, in <module>
    print(squares)
NameError: name 'squares' is not defined
    `}</samp></pre></div>
<hr/>
<h2 id="methods">Python Dictionary Methods</h2>

<p>Methods that are available with a dictionary are tabulated below. Some of them have already been used in the above examples.</p>

<div class="table-responsive">
	<table border="0">
		<tbody>
			<tr>
				<th>Method</th>
				<th>Description</th>
			</tr>
			<tr>
				<td>clear()</td>
				<td>Removes all items from the dictionary.</td>
			</tr>
			<tr>
				<td>copy()</td>
				<td>Returns a shallow copy of the dictionary.</td>
			</tr>
			<tr>
				<td>fromkeys(seq[, v])</td>
				<td>Returns a new dictionary with keys from <var>seq</var> and value equal to <var>v</var> (defaults to <code>None</code>).</td>
			</tr>
			<tr>
				<td>get(key[,d])</td>
				<td>Returns the value of the <var>key</var>. If the <var>key</var> does not exist, returns <var>d</var> (defaults to <code>None</code>).</td>
			</tr>
			<tr>
				<td>items()</td>
				<td>Return a new object of the dictionary's items in (key, value) format.</td>
			</tr>
			<tr>
				<td>keys()</td>
				<td>Returns a new object of the dictionary's keys.</td>
			</tr>
			<tr>
				<td>pop(key[,d])</td>
				<td>Removes the item with the <var>key</var> and returns its value or <var>d</var> if <var>key</var> is not found. If <var>d</var> is not provided and the <var>key</var> is not found, it raises <code>KeyError</code>.</td>
			</tr>
			<tr>
				<td>popitem()</td>
				<td>Removes and returns an arbitrary item (<strong>key, value</strong>). Raises <code>KeyError</code> if the dictionary is empty.</td>
			</tr>
			<tr>
				<td>setdefault(key[,d])</td>
				<td>Returns the corresponding value if the <var>key</var> is in the dictionary. If not, inserts the <var>key</var> with a value of <var>d</var> and returns <var>d</var> (defaults to <code>None</code>).</td>
			</tr>
			<tr>
				<td>update([other])</td>
				<td>Updates the dictionary with the key/value pairs from <var>other</var>, overwriting existing keys.</td>
			</tr>
			<tr>
				<td>values()</td>
				<td>Returns a new object of the dictionary's values</td>
			</tr>
		</tbody>
	</table>
</div>

<p>Here are a few example use cases of these methods.</p>
<div className='containers'>
<pre class="python-exec" style={{maxHeight: '600px'}}><code class="python hljs"><span class='comment'># Dictionary Methods</span><br />
marks = &#123;&#125;.fromkeys([<span class="hljs-string">'Math'</span>, <span class="hljs-string">'English'</span>, <span class="hljs-string">'Science'</span>], <span class="hljs-number">0</span>)<br />
<br />
<span class='comment'># Output: &#123;'English': 0, 'Math': 0, 'Science': 0 &#125;</span><br />
<span class="library">print</span>(marks)<br />
<br />
<span class="library">for</span> item <span class="library">in</span> marks.items():<br />
    <span class="library">print</span>(item)<br />
    <br />
<span class='comment'># Output: ['English', 'Math', 'Science']</span><br />
<span class="library">print</span>(list(sorted(marks.keys())))</code></pre><br />
</div>
<div className='output'>
<p><strong>Output</strong></p>

<pre><samp>{`{'Math': 0, 'English': 0, 'Science': 0}
('Math', 0)
('English', 0)
('Science', 0)
['English', 'Math', 'Science']`}</samp></pre></div>

<hr/>
<h2 id="comprehension">Python Dictionary Comprehension</h2>

<p>Dictionary comprehension is an elegant and concise way to create a new dictionary from an iterable in Python.</p>

<p>Dictionary comprehension consists of an expression pair (<strong>key: value</strong>) followed by a <code>for</code> statement inside curly braces <code>&#123;&#125;</code>.</p>

<p>Here is an example to make a dictionary with each item being a pair of a number and its square.</p>
<div className='containers'>
<pre class="python-exec" style={{maxHeight: '600px'}}><code class="python hljs"><span class='comment'># Dictionary Comprehension</span><br />
squares =  &#123;x: x*x <span class="library">for</span> x <span class="library">in</span> range(<span class="hljs-number">6</span>) &#125;<br />
<br />
<span class="library">print</span>(squares)</code></pre><br />
</div>
<div className='output'>
<p><strong>Output</strong></p>

<pre><samp>{`{0: 0, 1: 1, 2: 4, 3: 9, 4: 16, 5: 25}`}</samp></pre></div>

<p>This code is equivalent to</p>
<div className='containers'>
<pre class="python-exec" style={{maxHeight: '600px'}}><code class="python hljs">squares = &#123;&#125;<br />
<span class="library">for</span> x <span class="library">in</span> range(<span class="hljs-number">6</span>):<br />
    squares[x] = x*x<br />
<span class="library">print</span>(squares)</code></pre><br />
</div>
<div className='output'>
<p><strong>Output</strong></p>

<pre><samp>{`{0: 0, 1: 1, 2: 4, 3: 9, 4: 16, 5: 25}`}</samp></pre></div>

<p>A dictionary comprehension can optionally contain more for or if statements.</p>

<p>An optional <code>if</code> statement can filter out items to form the new dictionary.</p>

<p>Here are some examples to make a dictionary with only odd items.</p>
<br />
<div className='containers'>
<pre class="python-exec" style={{maxHeight: '600px'}}><code class="python hljs"><span class='comment'># Dictionary Comprehension with if conditional</span><br />
odd_squares =  &#123; &#123;x: x*x <span class="library">for</span> x <span class="library">in</span> range(<span class="hljs-number">11</span>) <span class="library">if</span> x % <span class="hljs-number">2</span> == <span class="hljs-number">1</span> &#123;<br />
<br />
<span class="library">print</span>(odd_squares)</code></pre><br />
</div>
<div className='output'>
<p><strong>Output</strong></p>

<pre><samp>{`{1: 1, 3: 9, 5: 25, 7: 49, 9: 81}`}</samp></pre></div>



<hr/>
<h2 id="other">Other Dictionary Operations</h2>

<h3 id="membership">Dictionary Membership Test</h3>

<p>We can test if a <code>key</code> is in a dictionary or not using the keyword <code>in</code>. Notice that the membership test is only for the <code>keys</code> and not for the <code>values</code>.</p>
<div className='containers'>
<pre class="python-exec" style={{maxHeight: '600px'}}><code class="python hljs"><span class='comment'># Membership Test for Dictionary Keys</span><br />
squares = &#123;<span class="hljs-number">1</span>: <span class="hljs-number">1</span>, <span class="hljs-number">3</span>: <span class="hljs-number">9</span>, <span class="hljs-number">5</span>: <span class="hljs-number">25</span>, <span class="hljs-number">7</span>: <span class="hljs-number">49</span>, <span class="hljs-number">9</span>: <span class="hljs-number">81</span> &#125;<br />
<br />
<span class='comment'># Output: True</span><br />
<span class="library">print</span>(<span class="hljs-number">1</span> <span class="library">in</span> squares)<br />
<br />
<span class='comment'># Output: True</span><br />
<span class="library">print</span>(<span class="hljs-number">2</span> <span class="library">not</span> <span class="library">in</span> squares)<br />
<br />
<span class='comment'># membership tests for key only not value</span><br />
<span class='comment'># Output: False</span><br />
<span class="library">print</span>(<span class="hljs-number">49</span> <span class="library">in</span> squares)</code></pre><br />
</div>
<div className='output'>
<p><strong>Output</strong></p>

<pre><samp>{`True
True
False`}</samp></pre></div>

<h3 id="iterate">Iterating Through a Dictionary</h3>

<p>We can iterate through each key in a dictionary using a <code>for</code> loop.</p>
<div className='containers'>
<pre class="python-exec" style={{maxHeight: '600px'}}><code class="python hljs"><span class='comment'># Iterating through a Dictionary</span><br />
squares =  &#123;<span class="hljs-number">1</span>: <span class="hljs-number">1</span>, <span class="hljs-number">3</span>: <span class="hljs-number">9</span>, <span class="hljs-number">5</span>: <span class="hljs-number">25</span>, <span class="hljs-number">7</span>: <span class="hljs-number">49</span>, <span class="hljs-number">9</span>: <span class="hljs-number">81</span> &#125;<br />
<span class="library">for</span> i <span class="library">in</span> squares:<br />
    <span class="library">print</span>(squares[i])</code></pre><br />
</div>
<div className='output'>
<p><strong>Output</strong></p>

<pre><samp>{`1
9
25
49
81`}</samp></pre></div>

<hr/>
<h3 id="built">Dictionary Built-in Functions</h3>

<p>Built-in functions like <code>all()</code>, <code>any()</code>, <code>len()</code>, <code>cmp()</code>, <code>sorted()</code>, etc. are commonly used with dictionaries to perform different tasks.</p>

<div class="table-responsive">
	<table border="0">
		<tbody>
			<tr>
				<th>Function</th>
				<th>Description</th>
			</tr>
			<tr>
				<td>all()</td>
				<td>Return <code>True</code> if all keys of the dictionary are True (or if the dictionary is empty).</td>
			</tr>
			<tr>
				<td>any()</td>
				<td>Return <code>True</code> if any key of the dictionary is true. If the dictionary is empty, return <code>False</code>.</td>
			</tr>
			<tr>
				<td>len(</td>
				<td>Return the length (the number of items) in the dictionary.</td>
			</tr>
			<tr>
				<td>cmp()</td>
				<td>Compares items of two dictionaries. (Not available in Python 3)</td>
			</tr>
			<tr>
				<td>sorted()</td>
				<td>Return a new sorted list of keys in the dictionary.</td>
			</tr>
		</tbody>
	</table>
</div>

<p>Here are some examples that use built-in functions to work with a dictionary.</p>

<pre class="python-exec" style={{maxHeight: '600px'}}><code class="python hljs"><span class='comment'># Dictionary Built-in Functions</span><br />
squares =  &#123;<span class="hljs-number">0</span>: <span class="hljs-number">0</span>, <span class="hljs-number">1</span>: <span class="hljs-number">1</span>, <span class="hljs-number">3</span>: <span class="hljs-number">9</span>, <span class="hljs-number">5</span>: <span class="hljs-number">25</span>, <span class="hljs-number">7</span>: <span class="hljs-number">49</span>, <span class="hljs-number">9</span>: <span class="hljs-number">81</span> &#125;<br />
<br />
<span class='comment'># Output: False</span><br />
<span class="library">print</span>(all(squares))<br />
<br />
<span class='comment'># Output: True</span><br />
<span class="library">print</span>(any(squares))<br />
<br />
<span class='comment'># Output: 6</span><br />
<span class="library">print</span>(len(squares))<br />
<br />
<span class='comment'># Output: [0, 1, 3, 5, 7, 9]</span><br />
<span class="library">print</span>(sorted(squares))</code></pre><br />
<div className='output'>
<p><strong>Output</strong></p>

<pre><samp>{`False
True
6
[0, 1, 3, 5, 7, 9]`}
</samp></pre></div>
  </div>

  
  
</div>

                              </div>
        </div>
    )
}

export default Dictionary
